/**
 * ArrayFieldEditor
 * Reusable component for editing array-type fields in section content.
 * Displays items as cards with drag-to-reorder, edit, and delete actions.
 */

import { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrayItemCard } from './ArrayItemCard'
import { ArrayItemDialog } from './ArrayItemDialog'
import type { FieldConfig } from '@limo/shared'

interface ArrayFieldEditorProps {
  field: FieldConfig
  value: unknown[]
  onChange: (value: unknown[]) => void
  accountId: string
}

/**
 * Get a singular label from a plural field label
 * e.g., "Features" -> "Feature", "Routes" -> "Route", "Boxes" -> "Box"
 */
function getSingularLabel(label: string): string {
  // Handle -ies -> -y (e.g., "Categories" -> "Category")
  if (label.endsWith('ies')) {
    return label.slice(0, -3) + 'y'
  }
  // Handle -es after consonants that need it (e.g., "Boxes" -> "Box", "Matches" -> "Match")
  // But NOT words like "Features" or "Routes" which just need -s removed
  if (label.endsWith('xes') || label.endsWith('ches') || label.endsWith('shes') || label.endsWith('sses')) {
    return label.slice(0, -2)
  }
  // Default: just remove trailing 's'
  if (label.endsWith('s')) {
    return label.slice(0, -1)
  }
  return label
}

export function ArrayFieldEditor({
  field,
  value,
  onChange,
  accountId,
}: ArrayFieldEditorProps) {
  // Ensure value is always an array
  const items = Array.isArray(value) ? value : []
  const schema = field.arrayItemSchema || []

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  // Get singular label for UI
  const singularLabel = getSingularLabel(field.label)

  // Generate stable IDs for items
  const itemIds = items.map((_, index) => `item-${index}`)

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end for reordering
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        const oldIndex = itemIds.indexOf(active.id as string)
        const newIndex = itemIds.indexOf(over.id as string)

        if (oldIndex !== -1 && newIndex !== -1) {
          const newItems = arrayMove([...items], oldIndex, newIndex)
          onChange(newItems)
        }
      }
    },
    [items, itemIds, onChange]
  )

  // Handle add new item
  const handleAdd = useCallback(() => {
    setEditingIndex(null)
    setDialogOpen(true)
  }, [])

  // Handle edit item
  const handleEdit = useCallback((index: number) => {
    setEditingIndex(index)
    setDialogOpen(true)
  }, [])

  // Handle delete item
  const handleDelete = useCallback(
    (index: number) => {
      const newItems = items.filter((_, i) => i !== index)
      onChange(newItems)
    },
    [items, onChange]
  )

  // Handle save item (add or update)
  const handleSaveItem = useCallback(
    (item: Record<string, unknown>) => {
      if (editingIndex !== null) {
        // Update existing item
        const newItems = [...items]
        newItems[editingIndex] = item
        onChange(newItems)
      } else {
        // Add new item
        onChange([...items, item])
      }
    },
    [editingIndex, items, onChange]
  )

  return (
    <div className="space-y-3">
      {/* Header with label and add button */}
      <div className="flex items-center justify-between">
        <Label>{field.label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add {singularLabel}
        </Button>
      </div>

      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}

      {/* Items list with drag-drop */}
      {items.length > 0 ? (
        <ScrollArea className={items.length > 5 ? 'h-[300px]' : undefined}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map((item, index) => (
                  <ArrayItemCard
                    key={itemIds[index]}
                    id={itemIds[index]}
                    item={item as Record<string, unknown>}
                    index={index}
                    schema={schema}
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                    itemLabel={singularLabel}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </ScrollArea>
      ) : (
        // Empty state
        <div className="text-center py-8 border border-dashed rounded-lg">
          <p className="text-muted-foreground text-sm">
            No {field.label.toLowerCase()} yet
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            className="mt-3"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add your first {singularLabel.toLowerCase()}
          </Button>
        </div>
      )}

      {/* Edit/Add dialog */}
      <ArrayItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        schema={schema}
        item={editingIndex !== null ? (items[editingIndex] as Record<string, unknown>) : null}
        onSave={handleSaveItem}
        accountId={accountId}
        itemLabel={singularLabel}
      />
    </div>
  )
}
